// Modal de "Actualizar datos" (baseline). Único modo: por RANGO DE FECHAS.
//
// Reutiliza DateRangeFilter y muestra un preview de los servicios SIN baseline
// en ese rango (GET list con has_baseline=false). Al lanzar:
// POST .../baseline/consolidate (async, 202), que ejecuta la lógica equivalente
// a `enqueue_repair_scales --mode=both` (repair + consolidate) sobre el rango.
// El backend es la autoridad; la validación de cliente es defensa en profundidad.
import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Label,
  FormGroup, Spinner,
} from "reactstrap"
import { toast } from "react-toastify"

import {
  getServiceValidationList,
  postServiceValidationBaselineConsolidate,
} from "../../helpers/api_rombo_helper"
import { dateRangeValue } from "./filterDefaults"
import DateRangeFilter from "./DateRangeFilter"

const BaselineConsolidateModal = ({ isOpen, toggle, authHeader, t, onConsolidated }) => {
  const today = useMemo(() => dateRangeValue("today"), [])
  const [range, setRange] = useState({
    range: "today",
    date_from: today.date_from,
    date_to: today.date_to,
  })
  const [preview, setPreview] = useState(null) // conteo de candidatos sin baseline
  const [previewLoading, setPreviewLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Lee el total de una respuesta de lista, tolerando ambos shapes:
  // página {items,total} o array plano.
  const readTotal = (resp) => {
    if (Array.isArray(resp)) return resp.length
    if (resp?.total != null) return Number(resp.total)
    if (Array.isArray(resp?.items)) return resp.items.length
    return 0
  }

  // Preview: cuenta los servicios SIN baseline en el rango de programación.
  useEffect(() => {
    if (!isOpen) return undefined
    let active = true

    if (!range.date_from || !range.date_to) {
      setPreview(null)
      return undefined
    }
    ;(async () => {
      setPreviewLoading(true)
      try {
        const token = await authHeader()
        const resp = await getServiceValidationList(token, {
          page: 1,
          size: 1,
          date_from: range.date_from,
          date_to: range.date_to,
          has_baseline: false,
        })
        if (active) setPreview(readTotal(resp))
      } catch (_e) {
        if (active) setPreview(null)
      } finally {
        if (active) setPreviewLoading(false)
      }
    })()
    return () => { active = false }
  }, [isOpen, range.date_from, range.date_to, authHeader])

  // Validación de cliente (defensa en profundidad; el backend es la autoridad).
  const validationError = useMemo(() => {
    if (!range.date_from || !range.date_to) {
      return t("SelectADateRange") || "Selecciona un rango de fechas"
    }
    if (range.date_to < range.date_from) {
      return t("InvalidDateRange") || "El rango de fechas es inválido"
    }
    return null
  }, [range.date_from, range.date_to, t])

  const resetAndClose = useCallback(() => {
    setPreview(null)
    toggle()
  }, [toggle])

  const onSubmit = useCallback(async () => {
    if (validationError) {
      toast.warn(validationError)
      return
    }
    const payload = { date_from: range.date_from, date_to: range.date_to }
    setSubmitting(true)
    try {
      const token = await authHeader()
      const resp = await postServiceValidationBaselineConsolidate(token, payload)
      const enqueued = resp?.total ?? resp?.enqueued ?? 0
      toast.success(
        (t("BaselineConsolidateEnqueued") ||
          "Se encolaron {n} consolidaciones (async). El baseline aparecerá en unos momentos.").replace(
          "{n}",
          enqueued,
        ),
      )
      if (typeof onConsolidated === "function") onConsolidated()
      resetAndClose()
    } catch (err) {
      const statusCode = err?.response?.status
      if (statusCode === 403) {
        toast.error(t("NoPermission") || "No tienes permiso para esta acción")
      } else if (statusCode === 400 || statusCode === 422) {
        const detail = err?.response?.data?.detail
        toast.error(
          typeof detail === "string"
            ? detail
            : t("InvalidRequest") || "Solicitud inválida",
        )
      } else {
        toast.error(t("UnexpectedError") || "Ocurrió un error inesperado")
      }
    } finally {
      setSubmitting(false)
    }
  }, [
    validationError, range.date_from, range.date_to,
    authHeader, onConsolidated, resetAndClose, t,
  ])

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        {t("UpdateData") || "Actualizar datos"}
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label className="form-label">
            {t("ScheduledDateRange") || "Rango de fecha de programación"}
          </Label>
          <DateRangeFilter t={t} value={range} onChange={setRange} />
          <div className="text-muted small mt-2">
            {previewLoading ? (
              <>
                <Spinner size="sm" className="me-1" />
                {t("Loading") || "Cargando…"}
              </>
            ) : preview != null ? (
              (t("ServicesWithoutBaselinePreview") ||
                "{n} servicios sin baseline en el rango").replace("{n}", preview)
            ) : null}
          </div>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={resetAndClose} disabled={submitting}>
          {t("Cancel") || "Cancelar"}
        </Button>
        <Button
          color="primary"
          onClick={onSubmit}
          disabled={submitting || !!validationError}
        >
          {submitting ? <Spinner size="sm" className="me-1" /> : null}
          {t("UpdateDataAction")}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

BaselineConsolidateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  authHeader: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  onConsolidated: PropTypes.func,
}

export default BaselineConsolidateModal
